<template>
  <div :id="id" :class="attachmentBase" style="position: relative">
    <!-- MIME TYPE ロード中 -->
    <template v-if="!mimeType">
      <loading-attachment ref="attachment" :fullscreen="false" />
    </template>

    <!-- PDF -->
    <template v-else-if="isPdf">
      <attachment-pdf
        :photostream="photostream"
        :pdfFront="pdfFront"
        :id="id"
        :path="path"
        :isMobile="isMobile"
        :downloadable="downloadable"
        :pdfThumbnail="pdfThumbnail"
        :isPdfThumbnail="isPdfThumbnail"
        :loading="loading"
        :imageFileName="imageFileName"
        :allowDownload="allowDownload"
        :downloadNote="downloadNote"
        :onLoadImage="onLoadImage"
        :pdfIconClick="pdfIconClick"
        :downloadFile="downloadFile"
        v-on:loadError="setDefaultIcon"
      />
    </template>

    <!-- 動画 -->
    <template v-else-if="isMovie">
      <attachment-movie
        :photostream="photostream"
        :id="id"
        :path="path"
        :isMobile="isMobile"
        :downloadable="downloadable"
        :thumbnailPath="thumbnailPath"
        :isMovieThumbnail="isMovieThumbnail"
        :loading="loading"
        :imageFileName="imageFileName"
        :allowDownload="allowDownload"
        :downloadNote="downloadNote"
        :onLoadImage="onLoadImage"
        :downloadFile="downloadFile"
        v-on:loadError="setDefaultIcon"
      />
    </template>

    <!-- Officeファイル -->
    <template v-else-if="isOffice">
      <attachment-office
        :photostream="photostream"
        :officeThumnail="officeThumnail"
        :loading="loading"
        :imageFileName="imageFileName"
        :allowDownload="allowDownload"
        :id="id"
        :isMobile="isMobile"
        :downloadable="downloadable"
        :attachment="image"
        :param="param"
        :downloadFile="downloadFile"
        :onLoadImage="onLoadImage"
      />
    </template>

    <!-- 画像 -->
    <template v-else>
      <attachment-photo
        :photostream="photostream"
        :isLoadError="isLoadError"
        :imagePath="imagePath"
        :loading="loading"
        :imageFileName="imageFileName"
        :allowDownload="allowDownload"
        :downloadNote="downloadNote"
        :onLoadImage="onLoadImage"
        :reload="reload"
        :downloadFile="downloadFile"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
.loading {
  /* ロード中 */
  min-height: 140px;
  min-width: 140px;
}
.preload {
  width: 100%;
  height: 100%;
}
.pdf-grey-box,
.movie-grey-box {
  width: 100%;
}
.list-icon-size {
  height: 72px;
  width: 60px;
}
.preview-icon-size {
  height: 96px;
}
.image-grey-box {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.default-tmb {
  display: grid;
  justify-items: center;
  align-items: center;
  width: 178px;
  height: 178px;
  margin-bottom: 8px;
  border-radius: 8px;
  position: relative;
  background-color: white;
}
.loaded-tmb {
  position: relative;
  margin-bottom: 8px;

  /* .other-photosとバッティングするため!importantを指定 */
  .pdf-tmb-label {
    top: 0 !important;
    left: 0 !important;
    transform: none !important;
    font-size: 10px !important;
    font-weight: bold !important;
    background: #dd1e03;
    color: white;
    padding: 0 2px !important;
    position: absolute;
    border-radius: 2px;
  }

  .movie-tmb-label {
    top: 0 !important;
    left: 0 !important;
    transform: none !important;
    font-size: 10px !important;
    font-weight: bold !important;
    background: black;
    color: white;
    padding: 0 2px !important;
    position: absolute;
    border-radius: 2px;
  }
}

.pdf-icon {
  width: 59px;
  height: 73px;
  object-fit: cover;
}

.pdf-tmb-preview {
  max-height: 250px !important;
  min-height: 187px !important;
  min-width: 156px !important;
  max-width: 300px !important;
}

.file-name {
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  text-align: center;
  color: white;

  &.dark {
    color: #707070;
  }
}

.attention-label {
  padding: 8px 4px 0 4px;
  color: white;
  font-size: 12px;
}

.guide-label {
  color: #aaa;
}

.dummyCover {
  height: 100%;
  z-index: 1;
}

.frame-border {
  max-width: 100%;
  position: relative;
  border: 0.5px black solid;
}
</style>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref, watch, type PropType } from "vue";
import S3AccessUtility from "./s3-acceess-utility";
import { v4 as uuidv4 } from "uuid";
import type { Attachment } from "../model";
import LoadingAttachment from "./loading/LoadingAttachment.vue";
import AttachmentPhoto from "./attachment/AttachmentPhoto.vue";
import AttachmentPdf from "./attachment/AttachmentPdf.vue";
import AttachmentMovie from "./attachment/AttachmentMovie.vue";
import AttachmentOffice from "./attachment/AttachmentOffice.vue";
import { getFileType, getDefaultIcon } from "./file-utility";
import type { ClientType } from "@/store/domain-store";
import type { DownloadDeviceTypes } from "@/suppport-attachment-types";
import axios from "axios";
import { allowFeature } from "../direct-app-config";

const PDF_DEFAULT_ICON = "/img/icon/icon_file_pdf.svg";
const MOVIE_DEFAULT_ICON = "/img/icon/icon_file_movie.svg";

export default defineComponent({
  name: "attachment-img",
  // NOTE: 旧コードでは PreviewPdfView（非同期）と CustomButton も登録されていたが、
  // テンプレート未使用のデッド登録だったため除去（振る舞い不変）。
  components: {
    LoadingAttachment,
    AttachmentPhoto,
    AttachmentPdf,
    AttachmentMovie,
    AttachmentOffice,
  },
  props: {
    image: { type: Object as PropType<Attachment>, default: undefined },
    param: {
      type: Object as PropType<{ messageId: string; commentId: string }>,
      default: () => ({ messageId: "", commentId: "" }),
    },
    // true: PhotoStreamから呼ばれている
    photostream: { type: Boolean, default: false },
    // ダウンロード可能か
    downloadable: { type: Boolean, default: false },
    // モバイルかの判定
    isMobile: { type: Boolean, default: false },
    clientType: {
      type: [String, Boolean] as PropType<ClientType>,
      default: false as unknown as ClientType,
      required: false,
    },
    allowSaveAttachments: {
      type: Object as PropType<DownloadDeviceTypes>,
      default: () => ({ web: true, ios: true, android: true, desktop: true }),
      required: false,
    },
  },
  setup(props, { emit }) {
    const width = ref("0");
    const height = ref("0");
    const size = ref(0);

    const path = ref("");
    const mimeType = ref("");
    const id = ref("");

    const loading = ref(true); //!< true ロード状態

    const pdfFront = ref(false); //!< true: PDF Viewer で表示

    const errorCounter = ref(10); //!< ファイル読み込み時のエラーリトライ回数
    const errorRetrySecond = ref(2); //!< エラーリトライ回数の間隔(秒)

    const thumbnailPath = ref(PDF_DEFAULT_ICON);

    // ローディング表示用の template ref（旧 this.$refs.attachment）
    const attachment = ref<HTMLImageElement>();

    const attachmentBase = computed((): Record<string, boolean> => {
      return {
        "d-flex": true,
        loading: loading.value,
        // "loading": true,
        "attachment-container": true,
        "w-100": true,
      };
    });

    const imagePath = computed((): string => path.value || props.image.url);

    // true: PDF
    const isPdf = computed((): boolean => getFileType(props.image) === "pdf");

    // true: Movie
    const isMovie = computed((): boolean => getFileType(props.image) === "video");

    // true: Office
    const isOffice = computed((): boolean => getFileType(props.image) === "office");

    // true: 画像ロードエラー
    const isLoadError = computed((): boolean => errorCounter.value < 0);

    const allowDownload = computed((): boolean => {
      if (!props.downloadable) {
        return false;
      } else {
        if (props.isMobile) {
          // ＊モバイル版ではダウンロードを規制
          return allowFeature("mobile-download");
        } else {
          return true;
        }
      }
    });

    const imageFileName = computed((): string => fileName(props.image.url));

    const isPdfThumbnail = computed((): boolean => {
      // return pdfThumbnail !== PDF_DEFAULT_ICON
      // PDFサムネは常にPDF_DEFAULT_ICONを返すので、常にfalse
      return false;
    });

    const isMovieThumbnail = computed((): boolean => thumbnailPath.value !== MOVIE_DEFAULT_ICON);

    const officeThumnail = computed((): string => getDefaultIcon(props.image));

    const pdfThumbnail = computed((): string => getDefaultIcon(props.image));

    const downloadNote = computed((): string => {
      const createNote = (): string => {
        if (props.allowSaveAttachments.web || props.allowSaveAttachments.desktop) {
          return `※ダウンロードは、${props.allowSaveAttachments.web ? "WindowsまたはMacのブラウザ" : ""}${props.allowSaveAttachments.web && props.allowSaveAttachments.desktop ? "・" : ""}${props.allowSaveAttachments.desktop ? "デスクトップアプリ" : ""}で対応しております。`;
        } else {
          return "";
        }
      };
      if (props.clientType === "android" || props.clientType === "ios") {
        if (props.clientType === "ios") {
          // iOSの場合
          if (props.allowSaveAttachments.ios) {
            // iOSのダウンロードが許可されているならば、説明文なし
            return "";
          } else {
            return createNote();
          }
        } else {
          // Androidの場合
          if (props.allowSaveAttachments.android) {
            // Androidのダウンロードが許可されているならば、説明文なし
            return "";
          } else {
            return createNote();
          }
        }
      } else {
        return "";
      }
    });

    const adjustSize = (): void => {
      const el = attachment.value;
      if (el) {
        width.value = String(el.width);
        height.value = String(el.height);
      }
    };

    const setDefaultIcon = (): void => {
      thumbnailPath.value = getDefaultIcon(props.image);
    };

    // S3リソースの場合は、URL解決とMIME-TYPE解決を行う
    const setImagePath = async (): Promise<void> => {
      try {
        const s3AccessUtility = S3AccessUtility.getInstance();
        if (props.image.mime === "application/pdf" || props.image.mime.startsWith("video/")) {
          thumbnailPath.value = String(
            await s3AccessUtility.getThumbFile(`${props.image.url}`, props.param, props.image.mime)
          );
        }
        if (props.photostream) {
          path.value = String(
            await s3AccessUtility.getFile(props.image.url, props.param, props.image.mime)
          );
          // ファイルのサイズを取得
          if (size.value == 0) {
            const response = await axios.get(path.value, {
              responseType: "blob",
            });
            size.value = response.data.size || 0;
            emit("loadedImage", size.value);
          }
        } else {
          path.value = String(
            await s3AccessUtility.getThumbFile(props.image.url, props.param, props.image.mime)
          );
        }
        mimeType.value = props.image.mime;
      } catch (err) {
        console.log("setImageError", err);
        path.value = "";
        thumbnailPath.value = "";
      }
    };

    const reload = (): void => {
      path.value = "";
      if (isLoadError.value == false) {
        // s3オブジ1ェクト再取得
        setTimeout(setImagePath, errorRetrySecond.value * 1000);
        loading.value = true;
        errorCounter.value -= 1;
      }
    };

    const onLoadImage = (): void => {
      loading.value = false;
      adjustSize();
    };

    // PDFアイコンクリック時
    const pdfIconClick = (): void => {
      pdfFront.value = !pdfFront.value;
    };

    function fileName(url: string): string {
      return S3AccessUtility.decodeName(url);
    }

    const downloadFile = async (): Promise<void> => {
      const s3AccessUtility = S3AccessUtility.getInstance();
      await s3AccessUtility.downloadFile(
        props.image.url,
        props.param,
        props.image.mime,
        imageFileName.value
      );
    };

    const clearPath = (): void => {
      path.value = "";
    };

    // 編集で画像が変わった時に発火
    watch(
      () => props.image,
      async () => {
        size.value = 0;
        await setImagePath();
      }
    );

    // created 相当（async・fire-and-forget）
    window.addEventListener("resize", adjustSize);
    id.value = uuidv4();
    void setImagePath();

    // NOTE: 旧コードには beforeDestory（typo・正しくは beforeDestroy）があり、Vue の
    // ライフサイクルとして呼ばれず resize リスナーが解除されない既存バグがあった。
    // クラス → Composition API 変換のこのタイミングで onBeforeUnmount により正しく解除する
    // （アンマウント後にリスナーが残っても adjustSize は no-op だが、リーク防止のため修正）。
    onBeforeUnmount(() => {
      window.removeEventListener("resize", adjustSize);
    });

    return {
      width,
      height,
      size,
      path,
      mimeType,
      id,
      loading,
      pdfFront,
      thumbnailPath,
      attachment,
      attachmentBase,
      imagePath,
      isPdf,
      isMovie,
      isOffice,
      isLoadError,
      allowDownload,
      imageFileName,
      isPdfThumbnail,
      isMovieThumbnail,
      officeThumnail,
      pdfThumbnail,
      downloadNote,
      setDefaultIcon,
      reload,
      onLoadImage,
      pdfIconClick,
      downloadFile,
      clearPath,
    };
  },
});
</script>
